import { DTIProps } from '@xometry/ui';
import { Empty, Form, FormInstance, Input, Select, Spin } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { FormItemProps } from 'antd/es/form/FormItem';
import clsx from 'clsx';
import { debounce, map, set } from 'es-toolkit/compat';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import omsApi from 'utils/axios';

export interface IOptionItem {
  value: unknown;
  label: React.ReactNode;
}

interface Props
  extends Partial<DTIProps>,
    Pick<FormItemProps, 'rules' | 'wrapperCol' | 'labelCol' | 'extra' | 'hidden'> {
  form: FormInstance;
  label?: React.ReactNode;
  name: string | string[];
  className?: string;
  endpoint: string;
  prefetch?: boolean;
  size?: SizeType;
  mode?: 'multiple' | 'tags' | undefined;
  defaultActiveFirstOption?: boolean;
  defaultValue?: IOptionItem | IOptionItem[];
  disabled?: boolean;
  customStyles?: FormItemProps['style'];
  placeholder?: string;
  allowClear?: boolean;
  searchValue?: string;
  params?: Record<string, string>;
  onChange?: (value: any) => void;
  onBlur?: () => void;
  onSearch?: (value: string, callback: () => void) => void;
  changeOptions?: (options?: IOptionItem[]) => IOptionItem[];
}

const Autocomplete = observer<Props>(
  ({
    form,
    label,
    name,
    endpoint,
    rules,
    disabled,
    hidden,
    prefetch,
    mode = undefined,
    size = 'small',
    defaultActiveFirstOption,
    className = 'ant-form-autocomplete',
    onChange,
    defaultValue = undefined,
    labelCol,
    wrapperCol,
    customStyles,
    extra,
    placeholder,
    allowClear = true,
    params,
    searchValue,
    onBlur,
    onSearch,
    changeOptions,
    'data-testid': DTI,
  }) => {
    const lastFetchId = useRef(0);
    const [collection, setCollection] = useState<IOptionItem[]>([]);
    const [value, setValue] = useState<IOptionItem | IOptionItem[] | undefined>();
    const [isFetching, setIsFetching] = useState(false);

    const fetchCollection = async (q: string) => {
      setIsFetching(true);

      lastFetchId.current += 1;
      const fetchId = lastFetchId.current;

      const response = await omsApi.get<{ payload: IOptionItem[] }>(endpoint, {
        params: { ...params, q },
      });

      if (fetchId !== lastFetchId.current) {
        return;
      }

      setCollection(changeOptions ? changeOptions(response?.data?.payload) : response?.data?.payload);
      setIsFetching(false);
    };

    const fetchCollectionDebouncedRef = useRef(debounce(fetchCollection, 800));

    const handleChange = (newOption: IOptionItem | IOptionItem[] | undefined) => {
      let newValue;

      if (Array.isArray(newOption)) {
        newValue = map(newOption, 'value');
      } else {
        newValue = newOption?.value;
      }

      setValue(newOption);
      const newValues = form.getFieldsValue(true) as object;
      const key = typeof name === 'object' ? name.join('.') : name;
      set(newValues, key, newValue);
      form.setFieldsValue(newValues);
      if (onChange) {
        onChange(newValue);
      }

      setIsFetching(false);
    };

    useEffect(() => {
      if (changeOptions) {
        setCollection(changeOptions());
      }

      if (prefetch) {
        void fetchCollection('');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const optionName = typeof name === 'object' ? `${name.join('_')}Option` : `${name}Option`;

    const handleSearch = (value: string) => {
      const callback = () => {
        void fetchCollectionDebouncedRef.current(value);
      };

      if (onSearch) {
        onSearch(value, callback);
      } else {
        callback();
      }
    };

    return (
      <div className={clsx(className, 'erp-autocomplete')}>
        <Form.Item hidden name={name}>
          <Input />
        </Form.Item>
        <Form.Item
          data-testid={DTI}
          label={label}
          name={optionName}
          rules={rules}
          wrapperCol={wrapperCol}
          labelCol={labelCol}
          initialValue={defaultValue}
          style={customStyles}
          extra={extra}
          hidden={hidden}
        >
          <Select
            data-testid={DTI && `${DTI}--Input`}
            defaultActiveFirstOption={defaultActiveFirstOption}
            labelInValue
            showSearch
            mode={mode}
            size={size}
            value={value}
            searchValue={searchValue}
            allowClear={allowClear}
            placeholder={placeholder || 'Type in to search'}
            notFoundContent={isFetching ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            filterOption={false}
            onChange={handleChange}
            onBlur={onBlur}
            onSearch={handleSearch}
            disabled={disabled}
            options={collection}
          />
        </Form.Item>
      </div>
    );
  },
);

export default Autocomplete;
